import { DocumentNode } from '@apollo/client';
import { authService } from '@features/auth/services/authService';
import siteConfig from '@config';
import { clientHeaders } from '@apollolocal/links';

interface GqlParams {
  query: DocumentNode | string;
  variables: Record<string, unknown>;
  clientName?: string;
  endpoint?: string;
  options?: RequestInit;
  /** Send this bearer token instead of asking Firebase for one. Used by the boot path
   *  that runs before the Firebase SDK has finished starting. */
  token?: string;
}

export class GraphqlError extends Error {
  constructor(error: Error) {
    super(error?.message); // Pass the error message to the parent class
    this.name = 'GraphqlError';
  }
}

/** A response whose body was not JSON: a 5xx page, or a firewall or proxy page in front of
 *  Hasura. Carries the status and content type, so a report says `403 text/html` rather than
 *  a bare `SyntaxError`. */
export class HttpResponseError extends Error {
  readonly status: number;
  readonly contentType: string | null;
  /** The first 500 characters of the body: enough to tell a Cloudflare page from a Hasura 5xx. */
  readonly bodySnippet: string;
  constructor(status: number, contentType: string | null, bodySnippet: string, cause: unknown) {
    super(`GraphQL endpoint answered HTTP ${status} (${contentType ?? 'no content type'}) with a body that is not JSON`);
    this.name = 'HttpResponseError';
    this.status = status;
    this.contentType = contentType;
    this.bodySnippet = bodySnippet;
    (this as { cause?: unknown }).cause = cause;
  }
}

async function fetchWithTimeout(resource: string, options: RequestInit = {}): Promise<Response> {
  const { timeout = 30000 } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const response = await fetch(resource, {
    ...options,
    signal: controller.signal,
  });
  clearTimeout(id);

  return response;
}

const execGraphqlWithFetch = async <T>({ query, variables, clientName, endpoint, options, token: givenToken }: GqlParams): Promise<T> => {
  if (!endpoint && siteConfig.graphqlHttpUrl) endpoint = siteConfig.graphqlHttpUrl;
  if (!clientName && siteConfig.graphqlClientName) clientName = siteConfig.graphqlClientName;
  if (!options) options = { cache: 'no-store' };
  if (!query) throw new Error('Missing query');
  if (!endpoint) throw new Error('Missing endpoint');

  const token = givenToken ?? (await authService.getAccessToken());
  const queryString = typeof query === 'string' ? query : query?.loc?.source?.body;

  // console.log('GQL query:', {
  //   query,
  //   queryString,
  //   variables,
  //   client: clientName,
  //   endpoint,
  //   options,
  // });

  const res = await fetchWithTimeout(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...clientHeaders(clientName!),
    } as HeadersInit,
    body: JSON.stringify({
      query: queryString,
      variables,
    }),
    ...options,
  });

  // Read the body as text first, so a body that is not JSON can still be reported.
  const bodyText = await res.text();
  let json;
  try {
    json = JSON.parse(bodyText);
  } catch (err) {
    throw new HttpResponseError(res.status, res.headers?.get?.('content-type') ?? null, bodyText.slice(0, 500), err);
  }

  if (json.error) {
    const gqlError = new GraphqlError(json.error);
    console.error(`GraphQL query or mutation failed:`, gqlError, {
      error: json.error,
      query,
      variables,
      // Never the token itself: Sentry keeps console lines as breadcrumbs.
      hasToken: Boolean(token),
    });
    // Sentry.Native.addBreadcrumb({
    //   category: 'graphql',
    //   message: 'GraphQL query or mutation failed',
    //   data: {
    //     query,
    //     variables,
    //     client,
    //     endpoint,
    //     options,
    //     error: json?.error,
    //   },
    // });
    // Sentry.Native.captureException(gqlError, scope => {
    //   scope.setTransactionName(`GraphQL query or mutation failed`);
    //   scope.setTags({
    //     ...variables,
    //     client,
    //     endpoint,
    //     ...options,
    //   });
    //   return scope;
    // });
  }

  if (json.errors) {
    const gqlError = new GraphqlError(json.errors[0]);
    console.error(`GraphQL query or mutation failed:`, gqlError, {
      errors: json.errors,
      query,
      variables,
      // Never the token itself: Sentry keeps console lines as breadcrumbs.
      hasToken: Boolean(token),
    });
    // Sentry.Native.addBreadcrumb({
    //   category: 'graphql',
    //   message: 'GraphQL query or mutation failed',
    //   data: {
    //     query,
    //     variables,
    //     client,
    //     endpoint,
    //     options,
    //     errors: json?.errors,
    //   },
    // });
    // Sentry.Native.captureException(gqlError, scope => {
    //   scope.setTransactionName(`GraphQL query or mutation failed`);
    //   scope.setTags({
    //     ...variables,
    //     client,
    //     endpoint,
    //     ...options,
    //   });
    //   return scope;
    // });
  }

  if (typeof query === 'string') return json;

  // Try to return just the data object if possible
  const rootResponseKey = (query as DocumentNode).definitions[0]?.selectionSet?.selections[0]?.name?.value;
  const rootResponse = json?.data[rootResponseKey];
  //console.log('Root response key:', { rootResponseKey, rootResponse });
  if (json?.data && json?.data[rootResponseKey]) return rootResponse;

  console.log(`Returning base JSON response:`, json);
  return json;
};

export { execGraphqlWithFetch };
export default execGraphqlWithFetch;
